import * as Sentry from '@sentry/react';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';

import {
  LOCAL_STORAGE_KEYS,
  IS_BYPASS_MODE,
  routes,
  SSO_CUSTOM_PROVIDER,
} from '../constants';

import { isAccessDeniedError, isSessionLostError } from './authRecovery';

/**
 * Amplify refuses to start a sign-in redirect while it still believes a user is
 * signed in (`assertUserNotAuthenticated` throws this). That is the norm in the
 * case we most need to recover: a token Amplify considers valid that the backend
 * rejects anyway. Signing out first is the only way through.
 */
const USER_ALREADY_AUTHENTICATED = 'UserAlreadyAuthenticatedException';

/**
 * Cap on redirect attempts per page load. The failure modes that reach the catch
 * below are mostly deterministic (a bad origin, a provider misconfiguration), so
 * an uncapped retry turns one broken config into a redirect attempt plus a Sentry
 * event for every failing poll, forever.
 */
const MAX_REAUTH_ATTEMPTS = 2;

// Coalesces concurrent failing requests so they don't each fire a redirect (a
// storm). Incremented BEFORE the await — that ordering is what makes it work for
// genuinely concurrent callers, not just sequential ones, and is pinned by a test.
let reauthAttempts = 0;
let reauthInFlight = false;
let reportedReauthFailure = false;

/** Test-only. Resets the per-page-load redirect guards. */
export const __resetReauthGuards = (): void => {
  reauthAttempts = 0;
  reauthInFlight = false;
  reportedReauthFailure = false;
};

const redirectToSso = async (): Promise<void> => {
  try {
    await signInWithRedirect({ provider: { custom: SSO_CUSTOM_PROVIDER } });
  } catch (error) {
    if ((error as { name?: unknown })?.name !== USER_ALREADY_AUTHENTICATED) {
      throw error;
    }
    // Clear the stale local session, then redirect for real. Without this the
    // `token_rejected_after_refresh` case — the one whose telemetry points at a
    // backend/pool misconfiguration — could never re-authenticate at all.
    await signOut();
    await signInWithRedirect({ provider: { custom: SSO_CUSTOM_PROVIDER } });
  }
};

/**
 * Central, loop-guarded reaction to a lost auth session. Called from every
 * data-layer error seam: the RTK Query base query, `asQueryFnResult`, the
 * `rtkQueryErrorLogger` middleware, and both reconnect catch-ups. Calling it
 * unconditionally from all of them is safe because at most one redirect is ever
 * in flight per page load; it is not free after a FAILED redirect, which is what
 * `MAX_REAUTH_ATTEMPTS` bounds.
 *
 * Fires only for a session that is actually gone (`SessionExpiredError`,
 * `SessionLostError`). It deliberately ignores:
 *  - `AuthTokenUnavailableError` — the normal pre-login load, where `AuthProvider`
 *    already runs the sign-in flow, so reacting here would race it;
 *  - `AccessDeniedError` — a 403 is an authorization verdict, not a session
 *    failure; re-authenticating cannot change group membership, and doing so would
 *    bounce the user between the app and the IdP forever (see
 *    `handleAccessDenied`).
 */
export const handleSessionExpired = async (error: unknown): Promise<void> => {
  if (IS_BYPASS_MODE) {
    return;
  }
  if (!isSessionLostError(error)) {
    return;
  }
  if (reauthInFlight || reauthAttempts >= MAX_REAUTH_ATTEMPTS) {
    return;
  }
  reauthInFlight = true;
  reauthAttempts += 1;

  try {
    await redirectToSso();
    // A successful redirect navigates away; the guard staying set is what stops a
    // concurrent batch of failures from queueing a second one behind it.
  } catch (reauthError) {
    // The redirect itself failed (e.g. InvalidOriginException, a deterministic
    // origin/config error that a reload will NOT fix). Release the guard so one
    // further attempt can happen, and report once so the broken recovery path is
    // visible without a per-request event storm.
    reauthInFlight = false;
    if (!reportedReauthFailure) {
      reportedReauthFailure = true;
      Sentry.captureException(reauthError, {
        tags: {
          category: 'auth',
          ['auth_error']: 'reauth_failed',
        },
      });
    }
  }
};

// Same shape of guard as the redirect above: one navigation per page load.
let accessDeniedHandled = false;

/** Test-only. Resets the access-denied navigation guard. */
export const __resetAccessDeniedGuard = (): void => {
  accessDeniedHandled = false;
};

/**
 * Reaction to a 403: the user is authenticated but not in the group the endpoint
 * requires.
 *
 * Sends them to the existing no-access page instead of leaving a board that
 * silently fails to load. It also clears the cached granted-groups snapshot,
 * because that cache is what let them reach the page at all —
 * `useSsoUserGroupAccess` falls back to the `localStorage` copy whenever the live
 * `granted-groups` response is empty, so a revoked membership keeps passing the
 * route guard until the stale copy is dropped.
 *
 * A full navigation rather than a router push: `shared` has no access to the
 * router, and this is a rare terminal state where losing in-memory state costs
 * nothing.
 */
export const handleAccessDenied = async (error: unknown): Promise<void> => {
  if (IS_BYPASS_MODE) {
    return;
  }
  if (!isAccessDeniedError(error)) {
    return;
  }
  if (accessDeniedHandled) {
    return;
  }
  accessDeniedHandled = true;

  try {
    globalThis.localStorage?.removeItem(LOCAL_STORAGE_KEYS.GRANTED_GROUPS);
  } catch {
    // A blocked storage is not a reason to skip the redirect.
  }

  if (globalThis.location?.pathname !== routes.NO_ACCESS) {
    globalThis.location?.assign(routes.NO_ACCESS);
  }
};

/**
 * The single seam every data-layer error handler calls. Routes an error to
 * whichever auth reaction applies (each handler ignores everything outside its own
 * case) and swallows nothing silently.
 *
 * Exists so the five call sites — the base query, `asQueryFnResult`, the
 * `rtkQueryErrorLogger` middleware and both reconnect catch-ups — cannot drift on
 * which handlers they invoke or on guarding the fire-and-forget. Synchronous by
 * design: callers are in `catch` blocks that must not await a redirect.
 */
export const recoverFromAuthFailure = (error: unknown): void => {
  const onHandlerError = (handlerError: unknown) =>
    Sentry.captureException(handlerError);
  void handleSessionExpired(error).catch(onHandlerError);
  void handleAccessDenied(error).catch(onHandlerError);
};
